/**
 * Pure validation function for the trip create/edit form.
 * No React, no side effects — input → output only.
 */
import type { TripFormState } from '../../types';
import { fromMantineDateTimeString } from '../mantineDateUtils';

export interface TripValidationResult {
  errors: Partial<Record<keyof TripFormState, string>>;
  isValid: boolean;
}

const CURRENCY_PATTERN = /^[A-Z]{3}$/;

export function validateTripForm(
  formData: TripFormState
): TripValidationResult {
  const errors: Partial<Record<keyof TripFormState, string>> = {};

  if (!formData.name.trim()) {
    errors.name = 'Name is required';
  }

  if (formData.lockEnabled) {
    if (!formData.lockStartDate) {
      errors.lockStartDate = 'Lock start date is required';
    }
    if (!formData.lockEndDate) {
      errors.lockEndDate = 'Lock end date is required';
    }
    if (formData.lockStartDate && formData.lockEndDate) {
      const lockStart = fromMantineDateTimeString(formData.lockStartDate);
      const lockEnd = fromMantineDateTimeString(formData.lockEndDate);
      if (lockStart && lockEnd && lockStart.getTime() >= lockEnd.getTime()) {
        errors.lockEndDate = 'Lock end must be after lock start';
      }
    }
  }

  if (formData.budgetEnabled) {
    const amount = parseFloat(formData.budgetAmount);
    if (Number.isNaN(amount) || amount < 0) {
      errors.budgetAmount = 'Budget must be a valid number ≥ 0';
    }
    if (!CURRENCY_PATTERN.test(formData.budgetCurrency.trim())) {
      errors.budgetCurrency = 'Currency must be a 3-letter ISO code';
    }
  }

  return { errors, isValid: Object.keys(errors).length === 0 };
}
